
import Vue from 'vue';
import Grid from '@/components/Grid.vue';
import ModalCrud from '@/components/ModalCrud.vue';
import { columns } from '@/utils/columns';
import api from '@/api/api';
import { M_MENU } from '@/store/mutations-types';
import type { IReturnConsultaComissoes, IResultConsultaComissoes } from '@/types/IDashComissoes';

export default Vue.extend({
	name: 'DashComissoes',
	data() {
		return {
			dataset: [] as IResultConsultaComissoes[],
			searchTerm: '',
			modals: {
				filtrarLancamentos: {
					isLoading: false,
					title: 'Filtrar',
					visible: false
				}
			},
			forms: {
				filtrarLancamentos: {
					controls: {
						isValid: true
					},
					data: {
						clientes: [],
						colaboradores: [],
						nTicket: '',
						dataInicio: '',
						dataFim: '',
						status: [],
						datasRefPagamento: [],
						ticketMonitorado: 'all',
						ticketMonitoradoAdm: 'all',
						recMonitorado: 'all',
					},
					datasets: {
						status: ['Liberado', 'Prevista'],
						clientes: [],
						colaboradores: [],
						datasRefPagamento: [],
						ticketMonitorado: [
							{
								id: 'all',
								label: 'Todos'
							},
							{
								id: 'isMonitored',
								label: 'Monitorado'
							},
							{
								id: 'noMonitored',
								label: 'Não monitorado'
							},
						],
						ticketMonitoradoAdm: [
							{
								id: 'all',
								label: 'Todos'
							},
							{
								id: 'isMonitored',
								label: 'Monitorado'
							},
							{
								id: 'noMonitored',
								label: 'Não monitorado'
							},
						],
						recMonitorado: [
							{
								id: 'all',
								label: 'Todos'
							},
							{
								id: 'isMonitored',
								label: 'Monitorado'
							},
							{
								id: 'noMonitored',
								label: 'Não monitorado'
							},
						]
					}
				}
			}
		}
	},
	methods: {
		handleSearch(value: string) {
			this.searchTerm = value;
		},
		parseCurrencyToNumber(value: string): number {
			if (!value) return 0;

			const normalized = value.replace(/[^\d,-]/g, '').replace(',', '.');
			const parsed = parseFloat(normalized);

			return isNaN(parsed) ? 0 : parsed;
		},
		formatCurrency(value: number): string {
			try {
				return new Intl.NumberFormat('pt-BR', {
					style: 'currency',
					currency: 'BRL',
					minimumFractionDigits: 2,
					maximumFractionDigits: 2
				}).format(value);
			} catch (error) {
				return `R$ ${value.toFixed(2).replace('.', ',')}`;
			}
		},
		nomeColaboradorGrupo(group: number): string {
			const item = this.filteredDataset.find((x) => x.idColaborador === group);

			return item ? item.nomeColaborador : '';
		},
		totalPorColaborador(group: number): string {
			const total = this.filteredDataset
				.filter((x) => x.idColaborador === group)
				.reduce((acc, item) => acc + this.parseCurrencyToNumber(item.aReceber), 0);

			return this.formatCurrency(total);
		},
		async loadData() {
			try {
				this.$store.state.loading = true;

				const { data } = await api.post<IReturnConsultaComissoes>('/comission-result', this.forms.filtrarLancamentos.data);

				if (data) {
					this.dataset = data.comissoes;
					this.forms.filtrarLancamentos.datasets.datasRefPagamento = data.datasRefPag;
				} else {
					this.dataset = [];
					this.forms.filtrarLancamentos.datasets.datasRefPagamento = [];
				};

				this.destroyFilter();
			} catch (error) {
				this.$toast.error(error.message ?? 'Falha na comunicação');
			} finally {
				this.$store.state.loading = false;
			}
		},
		async handleFilter() {
			try {
				this.modals.filtrarLancamentos.isLoading = true;
				this.modals.filtrarLancamentos.visible = true;
				const { data } = await api.get('/comission-result');

				this.forms.filtrarLancamentos.datasets = {
					...this.forms.filtrarLancamentos.datasets,
					...data
				};
			} catch (error) {
				this.$toast.error(error.message ?? 'Falha na comunicação');
			} finally {
				this.modals.filtrarLancamentos.isLoading = false;
			}
		},
		destroyFilter() {
			this.modals.filtrarLancamentos.visible = false;
			this.forms.filtrarLancamentos.datasets.clientes = [];
			this.forms.filtrarLancamentos.datasets.colaboradores = [];
		},
		async xlsxExport() {
			try {
				const { XlsxExport } = await import('@/utils/xlsxExport');
				const formattedColumns = columns.dashboards.consultaComissoes.map(x => ({
					name: x.value,
					label: x.text,
					isCurrency: x.isCurrency,
					isPercentage: x.isPercentage,
					isDate: x.isDate,
					isHourMin: x.isHourMin,
				}));

				formattedColumns.push(
					{
						name: 'ticketMonitorado',
						label: 'Ticket Monitorado',
						isCurrency: false,
						isPercentage: false,
						isDate: false,
						isHourMin: false,
					},
					{
						name: 'ticketMonitoradoAdm',
						label: 'Ticket Monitorado ADM',
						isCurrency: false,
						isPercentage: false,
						isDate: false,
						isHourMin: false,
					},
					{
						name: 'recMonitorado',
						label: 'Recebimento Monitorado',
						isCurrency: false,
						isPercentage: false,
						isDate: false,
						isHourMin: false,
					},
				);

				const formattedDataset = this.dataset.map(x => ({
					...x,
					ticketMonitorado: x.ticketMonitorado ? 'Sim' : 'Não',
					ticketMonitoradoAdm: x.ticketMonitoradoAdm ? 'Sim' : 'Não',
					recMonitorado: x.recMonitorado ? 'Sim' : 'Não'
				}))

				let instanceExport = new XlsxExport({
					columns: formattedColumns,
					data: formattedDataset,
					title: `Consulta de Comissões`
				});

				await instanceExport.export();
				instanceExport = null;
			} catch (error) {
				this.$toast.error(error.message ?? 'Falha ao comunicar com o banco de dados!');
			}
		},
	},
	components: {
		Grid,
		ModalCrud
	},
	computed: {
		filteredDataset(): IResultConsultaComissoes[] {
			if (!this.searchTerm) return this.dataset;

			const term = this.searchTerm.toString().toLocaleLowerCase();

			return this.dataset.filter((item) => {
				return Object.keys(item).some((key) => {
					const value = item[key];

					if (value === null || value === undefined || typeof value === 'boolean') return false;

					return value.toString().toLocaleLowerCase().indexOf(term) !== -1;
				});
			});
		},
	},
	async mounted() {
		this.$store.commit(M_MENU, 'Consulta de Comissões');
		await this.loadData();
	},
	setup() {
		return {
			columns
		}
	}
});

